import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { Trophy, X } from "@phosphor-icons/react";

/**
 * SeasonWelcomeBanner — celebrates the top 5 from the season that just ended.
 *
 * Shown on the first 7 days of a fresh season (Day 1-7) when
 * `season.previous_season_recap` is populated. Dismissal persisted in
 * localStorage per season so it doesn't nag returning players.
 */
export default function SeasonWelcomeBanner() {
  const [recap, setRecap] = useState(null);
  const [seasonNumber, setSeasonNumber] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get("/season/current").then((r) => {
      const rec = r.data?.previous_season_recap;
      if (rec) {
        setRecap(rec);
        setSeasonNumber(r.data?.season_number);
        try {
          const key = `nk_season_welcome_${r.data?.season_number}`;
          setDismissed(localStorage.getItem(key) === "1");
        } catch { /* ignore */ }
      }
    }).catch(() => {});
  }, []);

  if (!recap || dismissed) return null;
  const top5 = recap.top5 || [];
  if (top5.length === 0) return null;

  const close = () => {
    try { localStorage.setItem(`nk_season_welcome_${seasonNumber}`, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="nk-card p-5 border-l-2 border-nk-gold relative overflow-hidden"
      data-testid="season-welcome-banner"
    >
      <button
        onClick={close}
        aria-label="Dismiss welcome banner"
        data-testid="season-welcome-banner-close"
        className="absolute top-3 right-3 text-nk-mute hover:text-nk-text"
      >
        <X size={14} weight="bold" />
      </button>
      <div className="flex items-start gap-3 flex-wrap">
        <Trophy size={22} weight="fill" className="text-nk-gold shrink-0 mt-1" />
        <div className="flex-1 min-w-[220px]">
          <p className="eyebrow text-nk-gold mb-1">Season {seasonNumber} is live 🎉</p>
          <h3 className="font-display text-xl sm:text-2xl font-medium tracking-tight mb-3">
            Congratulations to the Season {recap.season_number} Top 5
          </h3>
          <ol className="space-y-2">
            {top5.map((p, i) => (
              <li
                key={p.user_id}
                data-testid={`welcome-top-${i + 1}`}
                className="flex items-center gap-3 text-sm"
              >
                <span className={`w-5 text-center font-mono font-bold ${
                  i === 0 ? "text-nk-gold" : i < 3 ? "text-nk-text" : "text-nk-mute"
                }`}>
                  {i === 0 ? "🏆" : `#${i + 1}`}
                </span>
                <AvatarIcon id={p.avatar} size={22} />
                <span className="font-medium text-nk-text">{p.username || "Player"}</span>
                {p.country && <CountryFlag country={p.country} size={12} />}
                <span className="ml-auto text-nk-accent font-mono">
                  {(p.season_score || 0).toLocaleString()} SS
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-nk-mute mt-4">
            A new 30-day season is under way. Climb hard — Season {seasonNumber}'s champions could be you.
          </p>
        </div>
      </div>
    </div>
  );
}
